import { Box, MenuItem, Select, Typography } from "@mui/material";
import React from "react";
import Track from "../../models/Track";
import UserAvatar from "../general/UserAvatar";
import { SONG_STATES, SONG_STATE_LABELS, SongSyncState } from "../../services/sync";
import { colors } from "../../theme";

interface songRow {
    index?: number;
    track: Track;
    /** Absent when the playlist isn't linked to the selected provider yet. */
    onStateChange?: (track: Track, state: SongSyncState) => void;
    stateDisabled?: boolean;
}

/**
 * Shared column template so the header and every row line up.
 * The "added by" column is dropped on narrow panes, where it would otherwise
 * collide with the artist column.
 *
 * Breakpoint is `sm` (600px), not `lg` (1200px) - this grid lives inside the
 * dashboard's main pane, which sits next to a ~260px sidebar, so the pane itself
 * is already narrower than the full viewport. At `lg` the column was hidden on
 * effectively every normal desktop window, not just genuinely narrow ones.
 */
export const TRACK_GRID = {
    xs: "24px 40px minmax(0, 3fr) minmax(0, 2fr) 132px",
    sm: "28px 44px minmax(0, 3fr) minmax(0, 2fr) minmax(0, 1.6fr) 148px",
};

/**
 * Applied to the "added by" cell in both the header and the rows - this is the
 * only place the breakpoint is declared. It used to also be hardcoded separately
 * inline on the row's avatar Box; the two drifted out of sync (header showing the
 * column while the row still hid it), which collapses the row to one fewer grid
 * item than the header has and shifts every column after it one slot to the left.
 */
export const ADDED_BY_CELL = { display: { xs: "none", sm: "flex" } } as const;

export default function SongRow(songRow: songRow) {
    const { index, track, onStateChange, stateDisabled } = songRow;
    // No stored state means the song is in the shared playlist but not in this
    // user's own - the same thing PENDING_ADD represents.
    const state: SongSyncState = track.state ?? "PENDING_ADD";

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: TRACK_GRID,
                alignItems: "center",
                gap: 2,
                px: 2,
                py: 1,
                borderRadius: 1,
                transition: "background-color 120ms ease",
                "&:hover": { backgroundColor: colors.elevatedHover },
            }}
        >
            <Typography sx={{ color: colors.textMuted, fontSize: 14, textAlign: "right" }}>
                {typeof index === "number" ? index + 1 : ""}
            </Typography>

            <Box
                component="img"
                src={track.imageUrl || "/assets/artist_example.png"}
                onError={e => { e.currentTarget.src = "/assets/artist_example.png"; }}
                alt=""
                sx={{ width: 44, height: 44, borderRadius: 1, objectFit: "cover" }}
            />

            <Typography noWrap title={track.title} sx={{ fontSize: 15, fontWeight: 600 }}>
                {track.title}
            </Typography>

            <Typography noWrap title={track.artist} sx={{ fontSize: 14, color: colors.textSecondary }}>
                {track.artist}
            </Typography>

            <Box sx={{ ...ADDED_BY_CELL, alignItems: "center", gap: 1, minWidth: 0 }}>
                <UserAvatar username={track.addedBy} imageUrl={track.addedByPhotoUrl} />
                <Typography noWrap sx={{ fontSize: 13, color: colors.textMuted }}>
                    {track.addedBy || track.source}
                </Typography>
            </Box>

            <Select
                size="small"
                value={state}
                disabled={stateDisabled || !onStateChange}
                onChange={(event) => onStateChange?.(track, event.target.value as SongSyncState)}
                sx={{
                    fontSize: 12,
                    borderRadius: 999,
                    backgroundColor: colors.elevated,
                    "& .MuiSelect-select": { py: 0.5, pl: 1.25 },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.line },
                }}
            >
                {SONG_STATES.map(option => (
                    <MenuItem key={option} value={option} sx={{ fontSize: 13 }}>
                        {SONG_STATE_LABELS[option]}
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
}
